import type { ChatFileMeta } from '@domain/chat/types';
import { getProto } from '../../im/pb-imports';

export type FileMessagePayload = {
  schema: 'locs.file.v2';
  version: 2;
  kind: 'file';
  provider: 's3';
  assetId: string;
  fileName: string;
  fileSize: number;
  sha256?: string;
  mimeType: string;
};

export type OnlineFileMessagePayload = {
  schema: 'locs.file.v3';
  version: 3;
  kind: 'file';
  provider: 'file-server';
  fileId: string;
  transferId: string;
  transport: 'online';
  fileName: string;
  fileSize: number;
  sha256?: string;
  mimeType: string;
};

function normalizeSha256(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.toLowerCase();
  return /^[a-f0-9]{64}$/.test(normalized) ? normalized : undefined;
}

function isFileMsgType(msgType?: number) {
  if (msgType == null) return false;
  return msgType === getProto().IM.BaseDefine.MsgType.MSG_TYPE_SINGLE_FILE;
}

export function buildFileMessagePayload(meta: {
  assetId: string;
  fileName: string;
  fileSize: number;
  sha256?: string;
  mimeType?: string | null;
}): FileMessagePayload {
  if (!meta.assetId) throw new Error('聊天文件缺少 assetId');
  const sha256 = normalizeSha256(meta.sha256);
  if (meta.sha256 && !sha256) throw new Error('文件 SHA-256 格式无效');
  return {
    schema: 'locs.file.v2',
    version: 2,
    kind: 'file',
    provider: 's3',
    assetId: meta.assetId,
    fileName: meta.fileName,
    fileSize: meta.fileSize,
    ...(sha256 ? { sha256 } : {}),
    mimeType: meta.mimeType || 'application/octet-stream',
  };
}

export function buildOnlineFileMessagePayload(meta: {
  fileId: string;
  transferId: string;
  fileName: string;
  fileSize: number;
  sha256?: string;
  mimeType?: string | null;
}): OnlineFileMessagePayload {
  if (!meta.fileId || !meta.transferId) throw new Error('实时传输缺少传输标识');
  const sha256 = normalizeSha256(meta.sha256);
  if (meta.sha256 && !sha256) throw new Error('文件 SHA-256 格式无效');
  return {
    schema: 'locs.file.v3',
    version: 3,
    kind: 'file',
    provider: 'file-server',
    fileId: meta.fileId,
    transferId: meta.transferId,
    transport: 'online',
    fileName: meta.fileName,
    fileSize: meta.fileSize,
    ...(sha256 ? { sha256 } : {}),
    mimeType: meta.mimeType || 'application/octet-stream',
  };
}

export function encodeFileMessagePayload(payload: FileMessagePayload | OnlineFileMessagePayload) {
  return new TextEncoder().encode(JSON.stringify(payload));
}

export function parseFileMessagePayload(bytes?: Uint8Array | null, msgType?: number): ChatFileMeta | null {
  if (!bytes || bytes.length === 0 || !isFileMsgType(msgType)) return null;
  try {
    const raw = JSON.parse(new TextDecoder().decode(bytes)) as any;
    if (raw.schema === 'locs.file.v3' && raw.provider === 'file-server' &&
        raw.version === 3 && raw.kind === 'file' && raw.transport === 'online' &&
        raw.fileId && raw.transferId && raw.fileName &&
        typeof raw.fileSize === 'number' && raw.fileSize > 0) {
      return {
        assetId: '',
        provider: 'file-server',
        fileName: raw.fileName,
        fileSize: raw.fileSize,
        mimeType: raw.mimeType || 'application/octet-stream',
        schemaVersion: 3,
        transport: 'online',
        fileId: raw.fileId,
        transferId: raw.transferId,
        ...(normalizeSha256(raw.sha256) ? { sha256: normalizeSha256(raw.sha256) } : {}),
      };
    }
    if (raw.schema !== 'locs.file.v2' || raw.provider !== 's3' || !raw.assetId ||
        raw.version !== 2 || raw.kind !== 'file' || !raw.fileName ||
        typeof raw.fileSize !== 'number' || raw.fileSize <= 0) return null;
    return {
      assetId: raw.assetId,
      provider: 's3',
      fileName: raw.fileName,
      fileSize: raw.fileSize,
      mimeType: raw.mimeType || 'application/octet-stream',
      schemaVersion: 2,
      transport: 'offline',
      ...(normalizeSha256(raw.sha256) ? { sha256: normalizeSha256(raw.sha256) } : {}),
    };
  } catch {
    return null;
  }
}

export function fileMessagePreview(meta: Pick<ChatFileMeta, 'fileName'>) {
  return `[文件] ${meta.fileName}`;
}
